import { Fragment, ReactElement, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Announcement,
  CreateAnnouncementData,
  ReorderAnnouncementData,
  UpdateAnnouncementData,
} from '@breathelife/types';
import { AddIcon, Box } from '@breathelife/mui';

import { ActionButton } from '../../../Components/Button/ActionButton';
import { DeleteAnnouncementModal } from './DeleteAnnouncementModal';
import { HelmetContainer } from '../../../Components/HelmetContainer';
import LoadingView from '../../../Components/LoadingView/LoadingView';
import { useDispatch, useSelector } from '../../../Hooks';
import {
  createAnnouncement,
  deleteAnnouncement,
  fetchAnnouncements,
  reorderAnnouncements,
  updateAnnouncement,
} from '../../../ReduxStore/Admin/AnnouncementsManagement/AnnouncementsOperations';
import {
  getAnnouncements,
  getAnnouncementsIsCreating,
  getAnnouncementsIsDeleting,
  getAnnouncementsIsFetching,
  getAnnouncementsIsUpdating,
} from '../../../ReduxStore/Admin/AnnouncementsManagement/AnnouncementsSelectors';
import { AnnouncementEditorDrawer } from './AnnouncementEditorDrawer';
import { AnnouncementsTable } from './AnnouncementsTable';

export function AnnouncementsManagementViewContainer(): ReactElement {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const announcements: Announcement[] = useSelector(getAnnouncements);
  const isFetching: boolean = useSelector(getAnnouncementsIsFetching);
  const isCreating: boolean = useSelector(getAnnouncementsIsCreating);
  const isUpdating: boolean = useSelector(getAnnouncementsIsUpdating);
  const isDeleting: boolean = useSelector(getAnnouncementsIsDeleting);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState<Announcement | undefined>(undefined);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  useEffect(() => {
    void dispatch(fetchAnnouncements());
  }, [dispatch]);

  const onOpenCreateAnnouncement = (): void => {
    setSelectedAnnouncement(undefined);
    setIsDrawerOpen(true);
  };

  const onCloseDrawer = useCallback((): void => {
    setIsDrawerOpen(false);
    setSelectedAnnouncement(undefined);
  }, []);

  const onRowClick = useCallback(
    (id?: string): void => {
      if (id) {
        const announcement = announcements.find((a: Announcement) => a.id === id);
        if (announcement) {
          setSelectedAnnouncement(announcement);
          setIsDrawerOpen(true);
        }
      }
    },
    [announcements],
  );

  const onSave = useCallback(
    (data: CreateAnnouncementData | UpdateAnnouncementData): void => {
      if (selectedAnnouncement) {
        void dispatch(updateAnnouncement(selectedAnnouncement.id, data as UpdateAnnouncementData, onCloseDrawer));
      } else {
        void dispatch(createAnnouncement(data as CreateAnnouncementData, onCloseDrawer));
      }
    },
    [dispatch, selectedAnnouncement, onCloseDrawer],
  );

  const onDeleteClick = useCallback((): void => {
    setIsDeleteDialogOpen(true);
  }, []);

  const onConfirmDelete = useCallback((): void => {
    if (selectedAnnouncement) {
      void dispatch(
        deleteAnnouncement(selectedAnnouncement.id, () => {
          setIsDeleteDialogOpen(false);
          onCloseDrawer();
        }),
      );
    }
  }, [dispatch, selectedAnnouncement, onCloseDrawer]);

  const onCancelDelete = useCallback((): void => {
    setIsDeleteDialogOpen(false);
  }, []);

  const onReorder = useCallback(
    (data: ReorderAnnouncementData[]): void => {
      void dispatch(reorderAnnouncements(data));
    },
    [dispatch],
  );

  const onTogglePublish = useCallback(
    (id: string, published: boolean): void => {
      void dispatch(updateAnnouncement(id, { published }));
    },
    [dispatch],
  );

  const isLoading = isCreating || isUpdating || isDeleting;

  return (
    <Fragment>
      <HelmetContainer text={t('pageTitles.announcements')} />
      {isFetching ? (
        <LoadingView />
      ) : (
        <Box p={3} width='100%'>
          <Box display='flex' justifyContent='flex-end' mb={2}>
            <ActionButton
              data-testid='addNewAnnouncementButton'
              color='primary'
              variant='contained'
              onClick={onOpenCreateAnnouncement}
              startIcon={<AddIcon htmlColor='white' />}
            >
              {t('cta.addNew')}
            </ActionButton>
          </Box>

          <AnnouncementsTable
            announcements={announcements}
            onRowClick={onRowClick}
            onReorder={onReorder}
            onTogglePublish={onTogglePublish}
          />
        </Box>
      )}

      <AnnouncementEditorDrawer
        isOpen={isDrawerOpen}
        onClose={onCloseDrawer}
        onSave={onSave}
        onDelete={onDeleteClick}
        announcement={selectedAnnouncement}
        isLoading={isLoading}
      />

      <DeleteAnnouncementModal
        isOpen={isDeleteDialogOpen}
        isLoading={isDeleting}
        announcementTitle={selectedAnnouncement?.title?.en || selectedAnnouncement?.title?.fr}
        onConfirm={onConfirmDelete}
        onCancel={onCancelDelete}
      />
    </Fragment>
  );
}
